/**
 * AI Workflow Task Form Component
 *
 * Interactive form for creating new AI workflow decomposition tasks.
 * Features:
 * - Task description textarea
 * - Industry/domain selector with common industries
 * - Role selector
 * - Company/organization input
 * - Constraint inputs (max cost, max time)
 * - Real-time validation
 * - Visual feedback during decomposition
 */

import React, { useState, useEffect, useRef } from 'react';
import {
  Briefcase,
  Building2,
  DollarSign,
  Clock,
  ChevronDown,
  AlertCircle,
  CheckCircle,
  Loader,
  Wrench,
  ChevronRight,
  FolderOpen,
  Upload,
  X,
  FileText
} from 'lucide-react';

import { useAIWorkflowDecompose, useAIWorkflowRetry } from '../hooks/useAIWorkflow';
import { instantiateTemplate } from '../api/templatesApi';
import FilePicker from './FilePicker';
import { smartUpload } from '../api/filesApi';
import PaymentRequired from './PaymentRequired';
import TaskBriefReview from './TaskBriefReview';
import TaskInputForm from './TaskInputForm';
import axios from 'axios';

/**
 * Common industries with descriptions
 */
const INDUSTRIES = [
  { value: 'healthcare', label: 'Healthcare', description: 'Medical, pharmaceutical, clinical' },
  { value: 'legal', label: 'Legal', description: 'Law firms, compliance, contracts' },
  { value: 'finance', label: 'Finance', description: 'Banking, investment, accounting' },
  { value: 'technology', label: 'Technology', description: 'Software, hardware, IT services' },
  { value: 'retail', label: 'Retail', description: 'E-commerce, stores, consumer goods' },
  { value: 'manufacturing', label: 'Manufacturing', description: 'Production, supply chain, logistics' },
  { value: 'education', label: 'Education', description: 'Schools, universities, training' },
  { value: 'marketing', label: 'Marketing', description: 'Advertising, branding, campaigns' },
  { value: 'real_estate', label: 'Real Estate', description: 'Property, development, management' },
  { value: 'consulting', label: 'Consulting', description: 'Professional services, advisory' },
  { value: 'government', label: 'Government', description: 'Public sector, policy, administration' },
  { value: 'nonprofit', label: 'Non-Profit', description: 'Charities, NGOs, foundations' },
  { value: 'other', label: 'Other', description: 'General purpose or mixed industries' }
];

/**
 * Common roles by industry
 */
const ROLES_BY_INDUSTRY = {
  healthcare: [
    'Physician',
    'Nurse',
    'Healthcare Administrator',
    'Clinical Researcher',
    'Medical Director',
    'Patient Care Coordinator'
  ],
  legal: [
    'Attorney',
    'Paralegal',
    'Legal Analyst',
    'Compliance Officer',
    'Contract Manager',
    'Legal Secretary'
  ],
  finance: [
    'Financial Analyst',
    'Accountant',
    'Investment Manager',
    'CFO',
    'Risk Manager',
    'Auditor'
  ],
  technology: [
    'Software Engineer',
    'Product Manager',
    'DevOps Engineer',
    'Data Scientist',
    'UX Designer',
    'Tech Lead'
  ],
  marketing: [
    'Marketing Manager',
    'Content Creator',
    'SEO Specialist',
    'Brand Manager',
    'Social Media Manager',
    'Campaign Manager'
  ],
  other: [
    'Executive',
    'Manager',
    'Analyst',
    'Coordinator',
    'Specialist',
    'Director'
  ]
};

/**
 * Industry selector dropdown
 */
function IndustrySelector({ value, onChange, error }) {
  const [isOpen, setIsOpen] = useState(false);
  const selectedIndustry = INDUSTRIES.find((i) => i.value === value);

  return (
    <div className="relative">
      <label className="block text-sm font-medium text-gray-700 mb-2">
        Industry / Domain
      </label>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full px-4 py-3 text-left bg-white border rounded-lg flex items-center justify-between transition-colors ${
          error ? 'border-red-300' : 'border-gray-300 hover:border-gray-400'
        }`}
      >
        <div className="flex items-center gap-2">
          <Briefcase size={18} className="text-gray-500" />
          <div>
            {selectedIndustry ? (
              <>
                <div className="font-medium text-gray-900">{selectedIndustry.label}</div>
                <div className="text-xs text-gray-500">{selectedIndustry.description}</div>
              </>
            ) : (
              <div className="text-gray-500">Select an industry...</div>
            )}
          </div>
        </div>
        <ChevronDown size={18} className={`text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {error && (
        <div className="mt-1 text-sm text-red-600 flex items-center gap-1">
          <AlertCircle size={14} />
          <span>{error}</span>
        </div>
      )}

      {isOpen && (
        <div className="absolute z-10 mt-2 w-full bg-white border border-gray-300 rounded-lg shadow-lg max-h-80 overflow-y-auto">
          {INDUSTRIES.map((industry) => (
            <button
              key={industry.value}
              type="button"
              onClick={() => {
                onChange(industry.value);
                setIsOpen(false);
              }}
              className={`w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors ${
                value === industry.value ? 'bg-blue-50' : ''
              }`}
            >
              <div className="font-medium text-gray-900">{industry.label}</div>
              <div className="text-xs text-gray-500">{industry.description}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Role selector dropdown
 */
function RoleSelector({ industry, value, onChange }) {
  const [isOpen, setIsOpen] = useState(false);
  const roles = ROLES_BY_INDUSTRY[industry] || ROLES_BY_INDUSTRY.other;

  return (
    <div className="relative">
      <label className="block text-sm font-medium text-gray-700 mb-2">
        Your Role <span className="text-gray-500 text-xs">(optional)</span>
      </label>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-4 py-3 text-left bg-white border border-gray-300 rounded-lg flex items-center justify-between hover:border-gray-400 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Briefcase size={18} className="text-gray-500" />
          <div className={value ? 'text-gray-900 font-medium' : 'text-gray-500'}>
            {value || 'Select a role...'}
          </div>
        </div>
        <ChevronDown size={18} className={`text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute z-10 mt-2 w-full bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
          <button
            type="button"
            onClick={() => {
              onChange('');
              setIsOpen(false);
            }}
            className="w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors text-gray-500 italic"
          >
            None
          </button>
          {roles.map((role) => (
            <button
              key={role}
              type="button"
              onClick={() => {
                onChange(role);
                setIsOpen(false);
              }}
              className={`w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors ${
                value === role ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-900'
              }`}
            >
              {role}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Constraints section
 */
function ConstraintsSection({
  constraints,
  onChange,
  defaultMaxRuns,
  onDefaultMaxRunsChange,
  defaultQualityThreshold,
  onDefaultQualityThresholdChange,
  maxDynamicDepth,
  onMaxDynamicDepthChange,
  maxDynamicWidth,
  onMaxDynamicWidthChange
}) {
  const [showAdvanced, setShowAdvanced] = useState(false);

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <label className="block text-sm font-medium text-gray-700">
          Constraints <span className="text-gray-500 text-xs">(optional)</span>
        </label>
        <button
          type="button"
          onClick={() => setShowAdvanced(!showAdvanced)}
          className="text-sm text-blue-600 hover:text-blue-700"
        >
          {showAdvanced ? 'Hide' : 'Show'} Advanced
        </button>
      </div>

      {showAdvanced && (
        <div className="space-y-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Maximum Cost (USD)
            </label>
            <div className="flex items-center gap-2">
              <DollarSign size={16} className="text-gray-500" />
              <input
                type="number"
                value={constraints.max_cost || ''}
                onChange={(e) => onChange({ ...constraints, max_cost: parseFloat(e.target.value) || undefined })}
                placeholder="e.g., 10.00"
                step="0.01"
                min="0"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Maximum Time (seconds)
            </label>
            <div className="flex items-center gap-2">
              <Clock size={16} className="text-gray-500" />
              <input
                type="number"
                value={constraints.max_time || ''}
                onChange={(e) => onChange({ ...constraints, max_time: parseInt(e.target.value) || undefined })}
                placeholder="e.g., 300"
                min="0"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Maximum Depth
            </label>
            <input
              type="number"
              value={constraints.max_depth || ''}
              onChange={(e) => onChange({ ...constraints, max_depth: parseInt(e.target.value) || undefined })}
              placeholder="e.g., 5"
              min="1"
              max="10"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Require Approval for Financial Decisions Over (USD)
            </label>
            <div className="flex items-center gap-2">
              <DollarSign size={16} className="text-gray-500" />
              <input
                type="number"
                value={constraints.financial_approval_threshold || ''}
                onChange={(e) =>
                  onChange({
                    ...constraints,
                    financial_approval_threshold: parseFloat(e.target.value) || undefined
                  })
                }
                placeholder="e.g., 1000.00"
                step="0.01"
                min="0"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Model Selection Preference
            </label>
            <select
              value={constraints.model_preference || 'balanced'}
              onChange={(e) => onChange({ ...constraints, model_preference: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            >
              <option value="cost_optimized">💰 Cost Optimized (40-60% cheaper)</option>
              <option value="balanced">⚖️ Balanced (Recommended) ⭐</option>
              <option value="quality_first">⭐ Quality First (Best results)</option>
            </select>
            <p className="text-xs text-gray-500 mt-1">
              Controls which AI models are used for workflow decomposition and execution
            </p>
          </div>

          {/* Quality & Agents */}
          <div className="border-t border-gray-200 pt-3 mt-3">
            <h4 className="text-xs font-semibold text-gray-700 uppercase mb-3">Quality & Agents</h4>

            <div className="space-y-3">
              {/* Max retries per node */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Max retries per node
                </label>
                <div className="flex items-center gap-3">
                  <input
                    type="range"
                    min="1"
                    max="5"
                    value={defaultMaxRuns}
                    onChange={(e) => onDefaultMaxRunsChange(parseInt(e.target.value))}
                    className="flex-1"
                  />
                  <span className="text-sm font-medium text-gray-700 w-6 text-center">{defaultMaxRuns}</span>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  How many times a node can re-run if its output doesn't meet the quality threshold
                </p>
              </div>

              {/* Quality threshold - shown when max_runs > 1 */}
              {defaultMaxRuns > 1 && (
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Quality threshold
                  </label>
                  <div className="flex items-center gap-3">
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.05"
                      value={defaultQualityThreshold}
                      onChange={(e) => onDefaultQualityThresholdChange(parseFloat(e.target.value))}
                      className="flex-1"
                    />
                    <span className="text-sm font-medium text-gray-700 w-10 text-center">{defaultQualityThreshold.toFixed(2)}</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Minimum self-evaluation score (0-1) a node must achieve before its output is accepted
                  </p>
                </div>
              )}

              {/* Sub-agent spawn depth */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Sub-agent spawn depth
                </label>
                <div className="flex items-center gap-3">
                  <input
                    type="range"
                    min="0"
                    max="3"
                    value={maxDynamicDepth}
                    onChange={(e) => onMaxDynamicDepthChange(parseInt(e.target.value))}
                    className="flex-1"
                  />
                  <span className="text-sm font-medium text-gray-700 w-6 text-center">{maxDynamicDepth}</span>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  How many levels deep nodes can spawn sub-agents (0 = no spawning)
                </p>
              </div>

              {/* Max children per node - shown when depth > 0 */}
              {maxDynamicDepth > 0 && (
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Max children per node
                  </label>
                  <div className="flex items-center gap-3">
                    <input
                      type="range"
                      min="1"
                      max="5"
                      value={maxDynamicWidth}
                      onChange={(e) => onMaxDynamicWidthChange(parseInt(e.target.value))}
                      className="flex-1"
                    />
                    <span className="text-sm font-medium text-gray-700 w-6 text-center">{maxDynamicWidth}</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Maximum number of child sub-agents a single node can spawn
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Available Capabilities Section
 * Displays model capabilities, modalities, and tools available for workflow planning
 */
function AvailableCapabilitiesSection() {
  const [showCapabilities, setShowCapabilities] = useState(false);
  const [capabilities, setCapabilities] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchCapabilities = async () => {
    if (capabilities) return; // Already loaded

    setLoading(true);
    setError(null);

    try {
      const response = await axios.get('/api/available-capabilities');
      setCapabilities(response.data);
    } catch (err) {
      console.error('Failed to fetch capabilities:', err);
      setError('Failed to load capabilities. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = () => {
    setShowCapabilities(!showCapabilities);
    if (!showCapabilities && !capabilities) {
      fetchCapabilities();
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <label className="block text-sm font-medium text-gray-700 flex items-center gap-2">
          <Wrench size={16} />
          Available Tools & Capabilities
          <span className="text-gray-500 text-xs font-normal">(for AI planner)</span>
        </label>
        <button
          type="button"
          onClick={handleToggle}
          className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
        >
          {showCapabilities ? 'Hide' : 'Show'}
          <ChevronRight size={14} className={`transform transition-transform ${showCapabilities ? 'rotate-90' : ''}`} />
        </button>
      </div>

      {showCapabilities && (
        <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
          {loading && (
            <div className="flex items-center justify-center py-8">
              <Loader className="animate-spin text-blue-600" size={24} />
              <span className="ml-2 text-sm text-gray-600">Loading capabilities...</span>
            </div>
          )}

          {error && (
            <div className="flex items-center gap-2 text-sm text-red-600 py-4">
              <AlertCircle size={16} />
              {error}
            </div>
          )}

          {capabilities && !loading && !error && (
            <div className="space-y-4">
              {/* Summary */}
              <div className="grid grid-cols-2 gap-3 p-3 bg-white rounded border border-gray-200">
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-600">{capabilities.summary.total_models}</div>
                  <div className="text-xs text-gray-600">AI Models</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600">{capabilities.summary.unique_capabilities}</div>
                  <div className="text-xs text-gray-600">Capabilities</div>
                </div>
              </div>

              {/* Input Modalities */}
              {capabilities.modalities.input.length > 0 && (
                <div>
                  <h4 className="text-xs font-semibold text-gray-700 mb-2 uppercase">Input Modalities</h4>
                  <div className="grid grid-cols-2 gap-2">
                    {capabilities.modalities.input.map((mod) => (
                      <div key={mod.id} className="p-2 bg-white rounded border border-gray-200 text-xs">
                        <div className="font-medium text-gray-900">{mod.name}</div>
                        <div className="text-gray-500">{mod.model_count} models</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Output Modalities */}
              {capabilities.modalities.output.length > 0 && (
                <div>
                  <h4 className="text-xs font-semibold text-gray-700 mb-2 uppercase">Output Modalities</h4>
                  <div className="grid grid-cols-2 gap-2">
                    {capabilities.modalities.output.map((mod) => (
                      <div key={mod.id} className="p-2 bg-white rounded border border-gray-200 text-xs">
                        <div className="font-medium text-gray-900">{mod.name}</div>
                        <div className="text-gray-500">{mod.model_count} models</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Special Capabilities */}
              {capabilities.capabilities.special.length > 0 && (
                <div>
                  <h4 className="text-xs font-semibold text-gray-700 mb-2 uppercase">Special Capabilities</h4>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {capabilities.capabilities.special.map((cap) => (
                      <div key={cap.id} className="p-2 bg-white rounded border border-gray-200 text-xs">
                        <div className="flex items-center justify-between mb-1">
                          <div className="font-medium text-gray-900">{cap.name}</div>
                          <div className="text-gray-500">{cap.model_count} models</div>
                        </div>
                        {cap.example_models.length > 0 && (
                          <div className="text-gray-500 text-xs">
                            Examples: {cap.example_models.join(', ')}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Supported Parameters */}
              {capabilities.supported_parameters.length > 0 && (
                <div>
                  <h4 className="text-xs font-semibold text-gray-700 mb-2 uppercase">Advanced Features</h4>
                  <div className="flex flex-wrap gap-2">
                    {capabilities.supported_parameters.map((param) => (
                      <span
                        key={param}
                        className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs font-medium"
                      >
                        {param.replace(/_/g, ' ')}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Help Text */}
              <div className="p-3 bg-blue-50 border border-blue-200 rounded">
                <div className="text-xs text-blue-800">
                  <div className="font-medium mb-1">ℹ️ How This Helps Planning</div>
                  <ul className="list-disc list-inside space-y-1 text-blue-700">
                    <li>AI planner sees available tools when breaking down your task</li>
                    <li>Automatically selects models with right capabilities</li>
                    <li>Can utilize vision, web search, PDF parsing, etc. as needed</li>
                  </ul>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Main task form component
 */
export default function AIWorkflowTaskForm({ onSuccess, initialTemplate }) {
  // Starter cards were generated FROM the user's domain/role — asking again is
  // redundant, and the card's domain vocabulary (FSM domains like "Sales")
  // doesn't exist in this form's industry option list anyway (brainstorm-z8t2).
  const isStarterCard = initialTemplate?.template_class === 'starter';
  // Set when a template load assigns industry, so the role-reset effect
  // doesn't wipe the role that was loaded alongside it.
  const skipRoleResetRef = useRef(false);
  const [taskDescription, setTaskDescription] = useState('');
  const [industry, setIndustry] = useState('');
  const [role, setRole] = useState('');
  const [company, setCompany] = useState('');
  const [constraints, setConstraints] = useState({});
  const [defaultMaxRuns, setDefaultMaxRuns] = useState(1);
  const [defaultQualityThreshold, setDefaultQualityThreshold] = useState(0.8);
  const [maxDynamicDepth, setMaxDynamicDepth] = useState(0);
  const [maxDynamicWidth, setMaxDynamicWidth] = useState(3);
  const [errors, setErrors] = useState({});
  const [loadedFromTemplate, setLoadedFromTemplate] = useState(false);
  const [taskBriefData, setTaskBriefData] = useState(null); // Phase 0 task brief
  const [templateInputValues, setTemplateInputValues] = useState({}); // typed fields from input_schema
  const [filePickerField, setFilePickerField] = useState(null); // which file_upload field is open
  const [fileUploading, setFileUploading] = useState({}); // fieldName → bool
  const fileInputRefs = useRef({}); // fieldName → <input type="file"> ref

  const {
    workflow,
    graph,
    loading,
    error: decomposeError,
    paymentRequired,
    decompose,
    reset
  } = useAIWorkflowDecompose();

  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const { retry, retrying } = useAIWorkflowRetry(paymentRequired?.workflowId);

  // Auto-show payment modal when 402 error occurs
  useEffect(() => {
    if (paymentRequired) {
      setShowPaymentModal(true);
    }
  }, [paymentRequired]);

  // Build effective task description — merge typed fields into prose if using input_schema
  const buildEffectiveDescription = () => {
    const schema = initialTemplate?.input_schema;
    if (!schema?.length) return taskDescription;
    const parts = schema
      .filter((f) => templateInputValues[f.name])
      .map((f) => {
        const v = templateInputValues[f.name];
        const display = v?.filename || v?.file_id || v;
        return `${f.label}: ${display}`;
      });
    const base = initialTemplate?.description || initialTemplate?.template_name || '';
    return parts.length ? `${base}\n\n${parts.join('\n')}` : taskDescription;
  };

  // Validate form
  const validate = () => {
    const newErrors = {};
    const hasTypedFields =
      initialTemplate?.input_schema?.length > 0 &&
      initialTemplate.input_schema
        .filter((f) => f.required)
        .every((f) => templateInputValues[f.name]?.toString().trim());

    if (!hasTypedFields) {
      if (!taskDescription.trim()) {
        newErrors.taskDescription = 'Task description is required';
      } else if (taskDescription.trim().length < 10) {
        newErrors.taskDescription = 'Task description must be at least 10 characters';
      }
    }

    if (!industry && !isStarterCard) {
      newErrors.industry = 'Please select an industry';
    }

    // Validate required typed fields
    if (initialTemplate?.input_schema?.length) {
      initialTemplate.input_schema.forEach((f) => {
        if (f.required && !templateInputValues[f.name]?.toString().trim()) {
          newErrors[`field_${f.name}`] = `${f.label} is required`;
        }
      });
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    // Full-graph template: skip decomposition, instantiate directly and execute
    if (initialTemplate?.graph_structure || initialTemplate?.id) {
      // Only use instantiate path for templates that have a saved graph
      // (user-saved templates always have graph_structure; system starter templates do not)
      const hasGraph = initialTemplate.graph_structure &&
        (initialTemplate.graph_structure.nodes?.length > 0);

      if (hasGraph) {
        try {
          const result = await instantiateTemplate(
            initialTemplate.id,
            templateInputValues,
            {
              taskDescription: buildEffectiveDescription(),
              company: company || undefined,
              domain: industry || undefined,
              role: role || undefined,
              constraints: Object.keys(constraints).length > 0 ? constraints : undefined,
              maxDynamicDepth: maxDynamicDepth,
              maxDynamicWidth: maxDynamicWidth,
              maxRuns: defaultMaxRuns,
              qualityThreshold: defaultMaxRuns > 1 ? defaultQualityThreshold : 0.8,
              execute: false,
            }
          );
          if (onSuccess && result.workflow && result.graph) {
            onSuccess({
              workflow: { ...result.workflow, status: 'ready' },
              graph: result.graph,
              metadata: {
                total_nodes: result.total_nodes,
                estimated_cost: result.estimated_cost,
              }
            });
          }
        } catch (err) {
          // Fall through to normal decompose if instantiate fails
          console.warn('instantiateTemplate failed, falling back to decompose:', err);
        }
        return;
      }
    }

    const effectiveDescription = buildEffectiveDescription();

    const result = await decompose({
      taskDescription: effectiveDescription,
      domain: industry || (isStarterCard ? initialTemplate?.domain : undefined),
      company: company || undefined,
      role: role || (isStarterCard ? initialTemplate?.role : undefined) || undefined,
      constraints: Object.keys(constraints).length > 0 ? constraints : undefined,
      defaultMaxRuns,
      defaultQualityThreshold: defaultMaxRuns > 1 ? defaultQualityThreshold : undefined,
      maxDynamicDepth,
      maxDynamicWidth: maxDynamicDepth > 0 ? maxDynamicWidth : undefined,
      useTaskBrief: true, // Enable Phase 0 by default
      usePremiumModel: false
    });

    if (result) {
      // Check if Phase 0 (task understanding) or direct workflow creation
      if (result.phase === 'task_understanding') {
        // Show task brief review UI
        setTaskBriefData(result);
      } else if (result.workflow && result.graph) {
        // Direct workflow creation (legacy mode or approved brief)
        if (onSuccess) {
          onSuccess({
            workflow: result.workflow,
            graph: result.graph,
            metadata: {
              total_nodes: result.total_nodes,
              estimated_cost: result.estimated_cost,
              estimated_duration_seconds: result.estimated_duration_seconds,
              risk_analysis: result.risk_analysis
            }
          });
        }
      }
    }
  };

  // Reset form
  const handleReset = () => {
    setTaskDescription('');
    setIndustry('');
    setRole('');
    setCompany('');
    setConstraints({});
    setDefaultMaxRuns(1);
    setDefaultQualityThreshold(0.8);
    setMaxDynamicDepth(0);
    setMaxDynamicWidth(3);
    setErrors({});
    setTaskBriefData(null);
    reset();
  };

  // Handle task brief approval
  const handleBriefApprove = (approvalResult) => {
    // Approval result now matches DecomposeTaskResponse structure:
    // { workflow: {...}, graph: {...}, total_nodes, total_edges, estimated_cost, ... }
    if (approvalResult.workflow && approvalResult.graph) {
      // Workflow was created and decomposed successfully
      if (onSuccess) {
        onSuccess({
          workflow: approvalResult.workflow,
          graph: approvalResult.graph || { nodes: [], edges: [] },
          metadata: {
            total_nodes: approvalResult.total_nodes,
            total_edges: approvalResult.total_edges,
            estimated_cost: approvalResult.estimated_cost,
            estimated_duration_seconds: approvalResult.estimated_duration_seconds,
            max_depth: approvalResult.max_depth,
            parallel_branch_count: approvalResult.parallel_branch_count,
            risk_analysis: approvalResult.risk_analysis,
            domain: approvalResult.metadata?.domain,
            role: approvalResult.metadata?.role
          }
        });
      }
      setTaskBriefData(null); // Clear brief data
    } else if (approvalResult.error) {
      // Approval succeeded but decomposition failed
      alert(`Workflow creation failed: ${approvalResult.error}`);
    }
  };

  // Handle task brief rejection - AI will re-analyze with feedback
  const handleBriefReject = (rejectionResult) => {
    // Rejection result contains the new iteration with updated brief
    if (rejectionResult.iteration) {
      setTaskBriefData(rejectionResult);
    }
  };

  // Handle task brief modification - AI will re-analyze with modifications
  const handleBriefModify = (modificationResult) => {
    // Modification result contains the new iteration with updated brief
    if (modificationResult.iteration) {
      setTaskBriefData(modificationResult);
    }
  };

  // Handle task brief cancellation
  const handleBriefCancel = () => {
    setTaskBriefData(null);
  };

  // Handle retry after payment
  const handleRetry = async () => {
    const result = await retry();
    if (result) {
      // Success! Workflow decomposed
      setShowPaymentModal(false);
      // The workflow state will update automatically via the hook
      if (onSuccess) {
        onSuccess({
          workflow: result.workflow,
          graph: result.graph,
          metadata: {
            total_nodes: result.total_nodes,
            estimated_cost: result.estimated_cost,
            estimated_duration_seconds: result.estimated_duration_seconds,
            risk_analysis: result.risk_analysis
          }
        });
      }
    } else if (decomposeError?.isPaymentError) {
      // Still insufficient - keep modal open
      alert('Balance still insufficient. Please top up more.');
    }
  };

  // Update role options when industry changes — but not when the change came
  // from loading a template that carries its own role.
  useEffect(() => {
    if (skipRoleResetRef.current) {
      skipRoleResetRef.current = false;
      return;
    }
    setRole('');
  }, [industry]);

  // Load template data when initialTemplate is provided
  useEffect(() => {
    if (initialTemplate && !loadedFromTemplate) {
      // Starter cards (brainstorm-z8t2.8): seed the task description from the
      // card's decomposition prompt rather than the generic description, and
      // stamp lineage constraints so the resulting task brief can be traced
      // back to the card it came from.
      if (initialTemplate.template_class === 'starter') {
        setTaskDescription(
          initialTemplate.constraints?.decomposition_prompt || initialTemplate.description || ''
        );
      } else if (!initialTemplate.input_schema?.length) {
        // Only pre-fill description for non-schema templates (freeform fallback)
        setTaskDescription(initialTemplate.description || '');
      }
      skipRoleResetRef.current = true;
      setIndustry(initialTemplate.domain || initialTemplate.industry || '');
      setRole(initialTemplate.role || '');
      if (initialTemplate.template_class === 'starter') {
        setConstraints({
          ...(initialTemplate.constraints || {}),
          source_template_id: initialTemplate.id,
          template_class: 'starter',
          card_title: initialTemplate.template_name,
          fsm_stage: initialTemplate.fsm_context?.stage,
        });
      } else {
        setConstraints(initialTemplate.constraints || {});
      }
      // Pre-populate typed fields with sample values if available
      if (initialTemplate.input_schema?.length && initialTemplate.sample_input) {
        setTemplateInputValues(initialTemplate.sample_input);
      }
      setLoadedFromTemplate(true);
    }
  }, [initialTemplate, loadedFromTemplate]);

  // Template with stored input_form_schema_v2: skip Phase 0 entirely.
  // Render TaskInputForm directly using the stored schema. On approve, call instantiateTemplate
  // with the filled values rather than the brief-approval endpoint.
  if (initialTemplate?.input_form_schema_v2 && !taskBriefData) {
    const syntheticBriefData = {
      iteration: {
        brief: {
          input_form_schema: initialTemplate.input_form_schema_v2,
          task_description: initialTemplate.description || initialTemplate.template_name || '',
          domain: initialTemplate.domain || '',
          role: initialTemplate.role || '',
        }
      }
    };
    const handleTemplateFormApproveOverride = async (formData, customisedSchema) => {
      try {
        const instResult = await instantiateTemplate(
          initialTemplate.id,
          formData || {},
          {
            taskDescription: initialTemplate.description || initialTemplate.template_name || '',
            company: company || undefined,
            domain: industry || initialTemplate.domain || undefined,
            role: role || initialTemplate.role || undefined,
            constraints: Object.keys(constraints).length > 0 ? constraints : undefined,
            maxDynamicDepth,
            maxDynamicWidth,
            maxRuns: defaultMaxRuns,
            qualityThreshold: defaultMaxRuns > 1 ? defaultQualityThreshold : 0.8,
            execute: false,
          }
        );
        if (onSuccess && instResult.workflow && instResult.graph) {
          onSuccess({
            workflow: { ...instResult.workflow, status: 'ready' },
            graph: instResult.graph,
            metadata: {
              total_nodes: instResult.total_nodes,
              estimated_cost: instResult.estimated_cost,
            }
          });
        }
      } catch (err) {
        console.error('Template instantiation failed:', err);
        alert(`Failed to instantiate template: ${err?.response?.data?.detail || err.message}`);
      }
    };
    return (
      <TaskInputForm
        taskBriefData={syntheticBriefData}
        onApproveOverride={handleTemplateFormApproveOverride}
        onCancel={() => onSuccess && onSuccess(null)}
      />
    );
  }

  // Show Phase 0 UI if active.
  // If the brief contains an input_form_schema, render the dynamic RJSF form first.
  // The form's onApprove either completes (workflow ready) or signals a regeneration
  // (_regenerated: true), in which case we update taskBriefData with the new iteration.
  if (taskBriefData) {
    const hasFormSchema = !!taskBriefData?.iteration?.brief?.input_form_schema;
    if (hasFormSchema) {
      return (
        <TaskInputForm
          taskBriefData={taskBriefData}
          onApprove={(result) => {
            if (result?._regenerated) {
              // Regeneration: new iteration returned — update brief data and re-render form
              setTaskBriefData(result);
            } else {
              handleBriefApprove(result);
            }
          }}
          onCancel={handleBriefCancel}
        />
      );
    }
    return (
      <TaskBriefReview
        taskBriefData={taskBriefData}
        onApprove={handleBriefApprove}
        onReject={handleBriefReject}
        onModify={handleBriefModify}
        onCancel={handleBriefCancel}
      />
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-lg p-6 max-w-3xl mx-auto">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Create AI Workflow</h2>
        <p className="text-gray-600">
          Describe your task and we'll break it down into an intelligent workflow
        </p>
        {loadedFromTemplate && initialTemplate && (
          <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center gap-2 text-blue-700">
            <CheckCircle size={18} />
            <span className="text-sm">
              Loaded from template: <span className="font-medium">{initialTemplate.template_name || initialTemplate.name}</span>
            </span>
          </div>
        )}
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Typed input fields (template with input_schema) */}
        {initialTemplate?.input_schema?.length > 0 ? (
          <div className="space-y-4">
            {initialTemplate.input_schema.map((field) => {
              const fieldError = errors[`field_${field.name}`];
              const inputClass = `w-full px-3 py-2.5 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm ${
                fieldError ? 'border-red-300' : 'border-gray-300'
              }`;
              return (
                <div key={field.name}>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    {field.label}{' '}
                    {field.required && <span className="text-red-500">*</span>}
                  </label>
                  {field.type === 'textarea' && (
                    <textarea
                      rows={3}
                      value={templateInputValues[field.name] || ''}
                      onChange={(e) =>
                        setTemplateInputValues((v) => ({ ...v, [field.name]: e.target.value }))
                      }
                      placeholder={field.placeholder || ''}
                      className={`${inputClass} resize-none`}
                    />
                  )}
                  {field.type === 'select' && (
                    <select
                      value={templateInputValues[field.name] || ''}
                      onChange={(e) =>
                        setTemplateInputValues((v) => ({ ...v, [field.name]: e.target.value }))
                      }
                      className={inputClass}
                    >
                      <option value="">Select…</option>
                      {(field.options || []).map((opt) => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                  )}
                  {field.type === 'file_upload' && (() => {
                    const selected = templateInputValues[field.name];
                    const isUploading = fileUploading[field.name];
                    const displayName = selected?.filename || selected || null;
                    return (
                      <div className="space-y-2">
                        {/* Selected file display */}
                        {displayName && (
                          <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg text-sm">
                            <FileText size={14} className="text-blue-600 shrink-0" />
                            <span className="text-blue-800 truncate flex-1">{displayName}</span>
                            <button
                              type="button"
                              onClick={() => setTemplateInputValues((v) => ({ ...v, [field.name]: '' }))}
                              className="text-blue-400 hover:text-blue-700 shrink-0"
                            >
                              <X size={14} />
                            </button>
                          </div>
                        )}
                        {/* Action buttons */}
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={() => setFilePickerField(field.name)}
                            className="flex items-center gap-1.5 px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-700"
                          >
                            <FolderOpen size={14} />
                            Choose from library
                          </button>
                          <button
                            type="button"
                            disabled={isUploading}
                            onClick={() => fileInputRefs.current[field.name]?.click()}
                            className="flex items-center gap-1.5 px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-700 disabled:opacity-50"
                          >
                            {isUploading ? <Loader size={14} className="animate-spin" /> : <Upload size={14} />}
                            {isUploading ? 'Uploading…' : 'Upload new'}
                          </button>
                        </div>
                        {/* Hidden file input for upload */}
                        <input
                          type="file"
                          className="hidden"
                          ref={(el) => { fileInputRefs.current[field.name] = el; }}
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            setFileUploading((u) => ({ ...u, [field.name]: true }));
                            try {
                              const result = await smartUpload(file, null, 100 * 1024 * 1024);
                              setTemplateInputValues((v) => ({
                                ...v,
                                [field.name]: { file_id: result.file_id, filename: file.name },
                              }));
                            } catch (err) {
                              console.error('File upload failed:', err);
                            } finally {
                              setFileUploading((u) => ({ ...u, [field.name]: false }));
                              e.target.value = '';
                            }
                          }}
                        />
                      </div>
                    );
                  })()}
                  {(field.type === 'text' || field.type === 'number' || field.type === 'date') && (
                    <input
                      type={field.type === 'number' ? 'number' : field.type === 'date' ? 'date' : 'text'}
                      value={templateInputValues[field.name] || ''}
                      onChange={(e) =>
                        setTemplateInputValues((v) => ({ ...v, [field.name]: e.target.value }))
                      }
                      placeholder={field.placeholder || ''}
                      className={inputClass}
                    />
                  )}
                  {fieldError && (
                    <div className="mt-1 text-xs text-red-600 flex items-center gap-1">
                      <AlertCircle size={12} />
                      <span>{fieldError}</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          /* Free-text task description */
          <div>
            <label htmlFor="taskDescription" className="block text-sm font-medium text-gray-700 mb-2">
              Task Description <span className="text-red-500">*</span>
            </label>
            <textarea
              id="taskDescription"
              value={taskDescription}
              onChange={(e) => setTaskDescription(e.target.value)}
              placeholder="Describe the task you want to accomplish in detail..."
              rows={4}
              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none ${
                errors.taskDescription ? 'border-red-300' : 'border-gray-300'
              }`}
            />
            {errors.taskDescription && (
              <div className="mt-1 text-sm text-red-600 flex items-center gap-1">
                <AlertCircle size={14} />
                <span>{errors.taskDescription}</span>
              </div>
            )}
            <div className="mt-1 text-xs text-gray-500">
              {taskDescription.length} characters
            </div>
          </div>
        )}

        {/* Starter cards carry their own domain/role — show them read-only
            instead of re-asking (brainstorm-z8t2). */}
        {isStarterCard ? (
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <Building2 size={15} className="text-gray-400" />
            <span>
              Using your profile: {initialTemplate.domain}
              {initialTemplate.role ? ` · ${initialTemplate.role}` : ''}
            </span>
          </div>
        ) : (
          <>
        {/* Industry Selector */}
        <IndustrySelector
          value={industry}
          onChange={setIndustry}
          error={errors.industry}
        />

        {/* Role Selector */}
        {industry && (
          <RoleSelector
            industry={industry}
            value={role}
            onChange={setRole}
          />
        )}
          </>
        )}

        {/* Company Name */}
        {!isStarterCard && (
        <div>
          <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-2">
            Company / Organization <span className="text-gray-500 text-xs">(optional)</span>
          </label>
          <div className="flex items-center gap-2">
            <Building2 size={18} className="text-gray-500" />
            <input
              id="company"
              type="text"
              value={company}
              onChange={(e) => setCompany(e.target.value)}
              placeholder="e.g., Acme Corporation"
              className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div className="mt-1 text-xs text-gray-500">
            Providing your organization name helps contextualize the workflow
          </div>
        </div>
        )}

        {/* Constraints */}
        <ConstraintsSection
          constraints={constraints}
          onChange={setConstraints}
          defaultMaxRuns={defaultMaxRuns}
          onDefaultMaxRunsChange={setDefaultMaxRuns}
          defaultQualityThreshold={defaultQualityThreshold}
          onDefaultQualityThresholdChange={setDefaultQualityThreshold}
          maxDynamicDepth={maxDynamicDepth}
          onMaxDynamicDepthChange={setMaxDynamicDepth}
          maxDynamicWidth={maxDynamicWidth}
          onMaxDynamicWidthChange={setMaxDynamicWidth}
        />

        {/* Available Capabilities */}
        <AvailableCapabilitiesSection />

        {/* Error message (excluding payment errors - shown in modal) */}
        {decomposeError && !paymentRequired && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-2 text-red-700">
            <AlertCircle size={20} />
            <div>
              <div className="font-medium">Decomposition Failed</div>
              <div className="text-sm">{decomposeError.message}</div>
            </div>
          </div>
        )}

        {/* Success message */}
        {workflow && !loading && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-2 text-green-700">
            <CheckCircle size={20} />
            <div>
              <div className="font-medium">Workflow Created Successfully!</div>
              <div className="text-sm">
                Generated {graph?.nodes?.length || 0} tasks
              </div>
            </div>
          </div>
        )}

        {/* Submit buttons */}
        <div className="flex gap-3 pt-4 border-t border-gray-200">
          <button
            type="submit"
            disabled={loading}
            className="flex-1 px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <Loader size={18} className="animate-spin" />
                <span>{initialTemplate?.graph_structure?.nodes?.length > 0 ? 'Starting…' : 'Decomposing...'}</span>
              </>
            ) : (
              <span>{initialTemplate?.graph_structure?.nodes?.length > 0 ? '▶ Run Workflow' : 'Create Workflow'}</span>
            )}
          </button>
          {(workflow || decomposeError) && (
            <button
              type="button"
              onClick={handleReset}
              className="px-6 py-3 bg-gray-200 text-gray-700 font-medium rounded-lg hover:bg-gray-300 transition-colors"
            >
              Reset
            </button>
          )}
        </div>
      </form>

      {/* Payment Required Modal */}
      {showPaymentModal && paymentRequired && (
        <PaymentRequired
          paymentData={paymentRequired}
          onRetry={handleRetry}
          onClose={() => setShowPaymentModal(false)}
        />
      )}

      {/* File Picker Modal — for file_upload fields */}
      <FilePicker
        show={!!filePickerField}
        onClose={() => setFilePickerField(null)}
        onSelect={(file) => {
          if (filePickerField) {
            setTemplateInputValues((v) => ({
              ...v,
              [filePickerField]: { file_id: file.file_id, filename: file.filename },
            }));
            setFilePickerField(null);
          }
        }}
        multiSelect={false}
      />
    </div>
  );
}
